import { LoadingOutlined } from '@ant-design/icons';
import { memo, useRef } from 'react';
import styled, { css } from 'styled-components';

import Logo from '../../assets/icons/Logo';
import theme from '../../assets/theme';
import { useDesignerUIContext } from '../../hooks/useDesignerUIState';
import useImage from '../../hooks/useImage';
import { LessonAttachment } from '../../services/backendService/backendService';
import { RoundMedia } from '../../services/backendService/types';
import EditorSlideViewer from './EditorSlideViewer';
import { PinCodeNudger } from './PinCodeNudger';
import { Slide, SlideElement } from './slide.types';
export const RICHTEXTEDITOR_ID_PREFIX = 'slate-editor-elementid-';

const EditorSlideWrapperViewer = ({
  aspect,
  hideLogo = false,
  slide,
  backgroundMedia,
  selectedElements,
  elementsToRender,
  gameId,
  lessonAttachments,
}: {
  aspect: {
    width: number;
    height: number;
  };
  hideLogo?: boolean;
  slide: Slide;
  backgroundMedia?: RoundMedia;
  selectedElements: SlideElement[] | null;
  elementsToRender: SlideElement[];
  gameId?: string;
  lessonAttachments?: LessonAttachment[];
}) => {
  const isDrawerOpen = useDesignerUIContext(context => context.state.isDrawerOpen);

  const containerRef = useRef<HTMLDivElement>(null);
  const maxContainerRef = useRef<HTMLDivElement>(null);

  const containerFieldRef = useRef<HTMLDivElement>(null);

  const { url: backgroundMediaUrl, isLoading: isLoadingBackground } = useImage({
    fileId: backgroundMedia?.fileId || null,
    type: backgroundMedia?.fileMimeType,
    cropConfig: backgroundMedia?.cropConfig,
    isAuthenticated: true,
    isDiscoverTemplate: false,
    isOwner: true,
  });

  if (elementsToRender === undefined) return null;
  return (
    <EditingAreaContainer>
      <SlideContainer>
        <MaxSizeContainer ref={maxContainerRef}>
          <FieldContainer
            ref={containerFieldRef}
            $alignItems={isDrawerOpen ? 'flex-start' : 'center'}
          >
            {/* Reference copntainer taht we observe for scale changes */}
            <RefContainer ref={containerRef} $aspectRatio={aspect.width / aspect.height}>
              {/* Background container that sets background image */}
              <BackgroundContainer
                $backgroundImageSrc={backgroundMediaUrl}
                $imageWidth={aspect.width}
                $brightness={backgroundMedia?.brightness}
                $isColorBackground={backgroundMedia?.fileMimeType === 'image/color'}
              >
                {/* Fixed height that sets the actual size of the slide */}
                <FixedHeightContainer $height={aspect.height} $width={aspect.width}>
                  <>
                    {gameId && (
                      <PinCodeNudger
                        gameId={gameId}
                        backgroundBrightness={slide.backgroundMedia?.brightness}
                      />
                    )}
                  </>

                  <EditorSlideViewer
                    elements={elementsToRender}
                    slide={slide}
                    selectedElements={selectedElements}
                    aspect={aspect}
                    lessonAttachments={lessonAttachments}
                  />
                </FixedHeightContainer>
                {/* Top right logo */}
                {!hideLogo && (
                  <LogoContainer>
                    <Logo />
                  </LogoContainer>
                )}
                {isLoadingBackground && (
                  <BackgroundLoaderContainer>
                    <LoadingOutlined style={{ fontSize: 20, color: 'black' }} spin />
                  </BackgroundLoaderContainer>
                )}
              </BackgroundContainer>
            </RefContainer>
          </FieldContainer>
        </MaxSizeContainer>
      </SlideContainer>
    </EditingAreaContainer>
  );
};

const EditingAreaContainer = styled.div`
  position: relative;
  width: 1920px;
  height: 1080px;
`;
const SlideContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const LogoContainer = styled.div`
  position: absolute;
  top: 0;
  width: 12%;
  height: 12%;
  right: 2%;
  color: ${theme.colors.orange};
  svg {
    width: 100%;
    height: 100%;
  }
`;

const MaxSizeContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const FieldContainer = styled.div<{
  $maxWidth?: number;
  $alignItems?: string;
}>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: ${({ $alignItems }) => $alignItems || 'center'};

  // Change the flex center css to scale horisontally or vertically
  ${({ $maxWidth }) => css`
    flex-direction: ${$maxWidth ? 'column' : 'row'};
    justify-content: center;
  `}
`;
const RefContainer = styled.div<{ $aspectRatio: number; $maxWidth?: number }>`
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  border-radius: 10px;
  position: relative;
  background-color: ${theme.colors.background};

  ${({ $maxWidth }) =>
    $maxWidth
      ? css`
          width: ${$maxWidth}px;
        `
      : css`
          width: 100%;
        `}
`;
const FixedHeightContainer = styled.div<{ $width: number; $height: number }>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  max-width: 100%;
  position: absolute;
  overflow: hidden;
  top: 0;
  left: 0;
`;

const BackgroundContainer = styled.div<{
  $brightness?: number | null;
  $isColorBackground: boolean;
  $imageWidth: number;
  $backgroundImageSrc?: string | null;
}>`
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.background};
  overflow: hidden;
  position: relative;
  border-radius: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  ${({ $backgroundImageSrc, $brightness, $isColorBackground }) =>
    $backgroundImageSrc && !$isColorBackground
      ? // Background media is selected
        css`
          background: url(${$backgroundImageSrc});
          ${(!$brightness || $brightness === 0.5) &&
            css`
              background: url(${$backgroundImageSrc});
              background-size: cover;
              background-position: center;
            `};
          ${$brightness &&
            $brightness > 0.5 &&
            css`
              background: linear-gradient(
                  rgba(255, 255, 255, ${$brightness - 0.5}),
                  rgba(255, 255, 255, ${$brightness - 0.5})
                ),
                url(${$backgroundImageSrc});
              background-size: cover;
              background-position: center;
            `};
          ${$brightness &&
            $brightness < 0.5 &&
            css`
              background: linear-gradient(
                  rgba(0, 0, 0, ${0.5 - $brightness}),
                  rgba(0, 0, 0, ${0.5 - $brightness})
                ),
                url(${$backgroundImageSrc});
              background-size: cover;
              background-position: center;
            `};
        `
      : $isColorBackground && $backgroundImageSrc
      ? // Color background is selected
        css`
          ${(!$brightness || $brightness === 0.5) &&
            css`
              background: ${$backgroundImageSrc};
            `};
          ${$brightness &&
            $brightness > 0.5 &&
            css`
              background: linear-gradient(
                  rgba(255, 255, 255, ${$brightness - 0.5}),
                  rgba(255, 255, 255, ${$brightness - 0.5})
                ),
                ${$backgroundImageSrc};
            `};
          ${$brightness &&
            $brightness < 0.5 &&
            css`
              background: linear-gradient(
                  rgba(0, 0, 0, ${0.5 - $brightness}),
                  rgba(0, 0, 0, ${0.5 - $brightness})
                ),
                ${$backgroundImageSrc};
            `};
        `
      : // No background is selected
        css`
          background: ${theme.colors.background};
        `};
  /* background-size: ${({ $imageWidth }) => $imageWidth}px auto; */
  background-repeat: no-repeat;
  height: 100%;
`;

const BackgroundLoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default memo(EditorSlideWrapperViewer);
